import React, { forwardRef, useEffect, useRef, useState } from "react";
import type { BaseProps } from "@purpur/common-types";
import lottie, { type AnimationItem } from "lottie-web/build/player/lottie_light";

export type AnimatedIllustrativeBaseIconProps = Omit<BaseProps, "children"> & {
  /**
   * Only available for animated icons.
   */
  autoplay?: boolean;
  /**
   * Delay in milliseconds before the animation starts.
   * Only available for animated icons.
   */
  delay?: number;
  /**
   * Only available for animated icons.
   */
  onAnimationEnd?: () => void;
};

export type AnimatedIllustrativeIconProps = {
  json: object;
} & AnimatedIllustrativeBaseIconProps;

export const AnimatedIllustrativeIcon = forwardRef<HTMLDivElement, AnimatedIllustrativeIconProps>(
  ({ json, autoplay = true, delay = 0, onAnimationEnd, ...props }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const animationRef = useRef<AnimationItem | null>(null);
    const [reduceMotion, setReduceMotion] = useState(false);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleReduceMotionChange = (event: any) => {
      setReduceMotion(event.matches);
    };

    useEffect(() => {
      const mediaQuery = window.matchMedia("(prefers-reduced-motion)");

      setReduceMotion(mediaQuery.matches);

      mediaQuery.addEventListener("change", handleReduceMotionChange);

      return () => {
        mediaQuery.removeEventListener("change", handleReduceMotionChange);
      };
    }, []);

    useEffect(() => {
      const loadAnimation = () => {
        if (containerRef.current) {
          const colorMap = {
            "rgb(153,10,227)": "--purpur-color-brand-core-purple",
            "rgb(41,0,62)": "--purpur-color-brand-deep-purple",
            "rgb(30,30,32)": "--purpur-color-brand-off-black",
          };

          animationRef.current = lottie.loadAnimation({
            container: containerRef.current,
            renderer: "svg",
            loop: false,
            autoplay: !reduceMotion && autoplay,
            animationData: json,
            rendererSettings: {
              preserveAspectRatio: "xMidYMid slice",
            },
          });

          // Updates SVG element attributes with CSS variable colors after the animation DOM is loaded.
          animationRef.current.addEventListener("DOMLoaded", () => {
            if (!containerRef.current) return;

            const rootStyles = getComputedStyle(document.documentElement);
            const svgElements = containerRef.current.querySelectorAll("svg *");

            svgElements.forEach((el) => {
              ["fill", "stroke"].forEach((attr) => {
                const currentColor = el.getAttribute(attr);

                if (currentColor && Object.prototype.hasOwnProperty.call(colorMap, currentColor)) {
                  const cssVar = rootStyles
                    .getPropertyValue(colorMap[currentColor as keyof typeof colorMap])
                    .trim();

                  if (cssVar) {
                    el.setAttribute(attr, cssVar);
                  }
                }
              });
            });
          });

          animationRef.current.addEventListener("complete", () => {
            onAnimationEnd?.();
          });

          if ((reduceMotion || !autoplay) && animationRef.current) {
            const totalFrames = animationRef.current.getDuration(true);
            animationRef.current.goToAndStop(totalFrames - 1, true);
          }
        }
      };

      const timeoutId = setTimeout(loadAnimation, delay);

      return () => {
        clearTimeout(timeoutId);
        animationRef.current?.destroy();
      };
    }, [reduceMotion, json, autoplay, onAnimationEnd, delay]);

    return (
      <div ref={ref} {...props}>
        <div ref={containerRef} />
      </div>
    );
  }
);

AnimatedIllustrativeIcon.displayName = "AnimatedIllustrativeIcon";
